// /src/api/authService.js
import axiosInstance from "./axiosInstance";
import { jwtDecode } from "jwt-decode";

// ──────────────────────────────────────────────────────────────
// LocalStorage keys
const AUTH_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_ROLE_KEY = "userRole";
const ADMIN_AUTH_TOKEN_KEY = "adminAuthToken";
const ADMIN_REFRESH_TOKEN_KEY = "adminRefreshToken";

// ──────────────────────────────────────────────────────────────
// Safe storage helper functions
const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch {}
};
const safeRemove = (key) => {
  try { localStorage.removeItem(key); } catch {}
};

// ──────────────────────────────────────────────────────────────
// Helper for extracting role from JWT
const decodeRoleFromJWT = (token) => {
  try {
    const payload = jwtDecode(token) || {};
    if (payload.role) return payload.role;
    if (payload.sub && typeof payload.sub === "object" && payload.sub.role) {
      return payload.sub.role;
    }
  } catch {}
  return undefined;
};

// ──────────────────────────────────────────────────────────────
// Token getters and setters
const setToken = (token, type) => {
  if (!token) return;
  const role = decodeRoleFromJWT(token);

  // map ให้ตรงกับคีย์ที่ประกาศไว้
  if (role === "admin") {
    if (type === AUTH_TOKEN_KEY) return safeSet(ADMIN_AUTH_TOKEN_KEY, token);
    if (type === REFRESH_TOKEN_KEY) return safeSet(ADMIN_REFRESH_TOKEN_KEY, token);
  }
  safeSet(type, token);
};

export const getAuthToken = () => safeGet(AUTH_TOKEN_KEY);
export const getRefreshToken = () => safeGet(REFRESH_TOKEN_KEY);
export const getUserRole = () => safeGet(USER_ROLE_KEY);
export const getAdminAuthToken = () => safeGet(ADMIN_AUTH_TOKEN_KEY);
export const getAdminRefreshToken = () => safeGet(ADMIN_REFRESH_TOKEN_KEY);

export const saveAuthToken = (token) => setToken(token, AUTH_TOKEN_KEY);
export const saveRefreshToken = (token) => setToken(token, REFRESH_TOKEN_KEY);
export const saveUserRole = (role) => safeSet(USER_ROLE_KEY, role);

export const clearTokens = () => {
  safeRemove(AUTH_TOKEN_KEY);
  safeRemove(REFRESH_TOKEN_KEY);
  safeRemove(USER_ROLE_KEY);
  safeRemove(ADMIN_AUTH_TOKEN_KEY);
  safeRemove(ADMIN_REFRESH_TOKEN_KEY);
  if (typeof window !== "undefined" && window.location) window.location.hash = "#/login";
};

// ──────────────────────────────────────────────────────────────
// Refresh Access Token (ใช้ axiosInstance + relative path)
export const refreshAccessToken = async () => {
  const rt = getRefreshToken();
  if (!rt) return null;

  try {
    const { data } = await axiosInstance.post(
      `/auth/refresh`,
      { refresh_token: rt },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    const { access_token, refresh_token } = data || {};
    if (!access_token) return null;

    saveAuthToken(access_token);
    saveRefreshToken(refresh_token || rt);
    return access_token;
  } catch {
    return null;
  }
};

// ──────────────────────────────────────────────────────────────
// Authentication APIs (using axiosInstance for API calls)
export const login = async (email, password) => {
  const body = { email: String(email || "").trim(), password: String(password ?? "") };
  const { data } = await axiosInstance.post(`/auth/login`, body, {
    headers: { "Content-Type": "application/json" },
  });

  const { access_token, refresh_token, user } = data || {};
  if (!access_token || !refresh_token || !user?.role) {
    throw new Error("Invalid login response.");
  }

  saveAuthToken(access_token);
  saveRefreshToken(refresh_token);
  saveUserRole(user.role);
  return { access_token, refresh_token, role: user.role };
};

export const logout = () => clearTokens();

export const register = async (userData) => {
  const { email, password, first_name, last_name, phone_number } = userData || {};
  const body = {
    email: String(email || "").trim().toLowerCase(),
    first_name: String(first_name || "").trim(),
    last_name: String(last_name || "").trim(),
    phone_number: String(phone_number || "").trim(),
    ...(password ? { password: String(password) } : {}),
  };

  const { data } = await axiosInstance.post(`/auth/register`, body, {
    headers: { "Content-Type": "application/json" },
  });

  return data;
};

export const requestPasswordReset = async (email) => {
  const { data } = await axiosInstance.post(
    `/auth/forgot-password`,
    { email: String(email || "").trim().toLowerCase() },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
};

export const resetPassword = async (token, newPassword) => {
  const { data } = await axiosInstance.post(
    `/auth/reset-password`,
    { token, new_password: newPassword },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
};

export const fetchAuthenticatedUser = async () => {
  const token = getAuthToken();
  const { data } = await axiosInstance.get(`/user/me`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  return data;
};

// ──────────────────────────────────────────────────────────────
// Login with Google (ID Token → /api/auth/google)
export const loginWithGoogle = async (idToken, ctx = {}) => {
  if (!idToken) throw new Error("Missing Google id_token");

  const body = {
    id_token: String(idToken).trim(),
    ...(ctx.next ? { next: String(ctx.next) } : {}),
    ...(ctx.source_page ? { source_page: String(ctx.source_page) } : {}),
    ...(ctx.utm_source ? { utm_source: String(ctx.utm_source) } : {}),
    ...(ctx.utm_medium ? { utm_medium: String(ctx.utm_medium) } : {}),
    ...(ctx.utm_campaign ? { utm_campaign: String(ctx.utm_campaign) } : {}),
  };

  const { data } = await axiosInstance.post(`/auth/google`, body, {
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });

  const { access_token, refresh_token, user } = data || {};
  if (!access_token || !refresh_token || !user?.role) {
    throw new Error("Invalid Google login response.");
  }

  saveAuthToken(access_token);
  saveRefreshToken(refresh_token);
  saveUserRole(user.role);

  return {
    access_token,
    refresh_token,
    role: user.role,
    created: Boolean(data?.created),
    redirect_to: data?.redirect_to,
  };
};
